/**
 * FirmEventChips — renders firm-calendar events (holidays, all-staff meetings)
 * as a compact color-coded strip inside a day cell or day/week column.
 *
 * Visually distinct from appointments — always a saturated coloured strip with
 * a light background so billers/providers can instantly tell "this isn't a
 * patient session, it's a firm-wide thing".
 */
import React from 'react';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../ui/tooltip';

export const FirmEventChips = ({ events, compact = true, onClick }) => {
  if (!events || events.length === 0) return null;

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-0.5" data-testid="firm-event-chips">
        {events.map((ev) => {
          const color = ev.color || '#0ea5e9';
          const timeRange = !ev.all_day && ev.start_time
            ? `${ev.start_time}${ev.end_time ? `–${ev.end_time}` : ''}`
            : null;
          // Weekend-observance variants:
          //  * _is_shadow    → actual calendar date (Sat/Sun) — render grayed out
          //  * _is_observed  → shifted weekday — label with "(Observed)"
          const isShadow = !!ev._is_shadow;
          const isObserved = !!ev._is_observed;
          const displayTitle = isObserved ? `${ev.title} (Observed)` : ev.title;
          return (
            <Tooltip key={`${ev.id}-${ev.occurrence_date}${isObserved ? '-obs' : ''}`}>
              <TooltipTrigger asChild>
                <div
                  className={`flex items-center gap-1 rounded px-1 ${compact ? 'text-[10px] py-0.5' : 'text-xs py-1'} font-medium truncate cursor-default ${isShadow ? 'opacity-50 italic' : ''}`}
                  style={{
                    backgroundColor: isShadow ? `${color}11` : `${color}22`,
                    borderLeft: `3px solid ${color}`,
                    color: '#0f172a',
                  }}
                  onClick={onClick ? (e) => { e.stopPropagation(); onClick(ev); } : undefined}
                  data-testid={`firm-event-chip-${ev.id}${isObserved ? '-observed' : isShadow ? '-shadow' : ''}`}
                >
                  <span className="truncate">{displayTitle}</span>
                </div>
              </TooltipTrigger>
              <TooltipContent side="top" align="start" className="max-w-xs">
                <div className="space-y-1">
                  <p className="font-semibold text-sm leading-tight">{ev.title}</p>
                  {ev.type_name && <p className="text-xs opacity-80">{ev.type_name}</p>}
                  {isShadow && ev._observed_on && (
                    <p className="text-xs text-amber-300">Falls on a weekend — observed on {ev._observed_on}</p>
                  )}
                  {isObserved && ev._actual_date && (
                    <p className="text-xs text-amber-300">Actual date: {ev._actual_weekday}, {ev._actual_date} — office closed this day instead</p>
                  )}
                  {ev.description && <p className="text-xs opacity-90 whitespace-pre-wrap">{ev.description}</p>}
                  {timeRange && <p className="text-xs opacity-80">{timeRange}</p>}
                  {ev.blocks_booking && !isShadow && <p className="text-xs text-red-300 font-medium">Blocks booking</p>}
                </div>
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
};

export default FirmEventChips;
